<!-- This component displays checkout elements for logged in users, such as Management Questions/On Account Payments for B2B.
@prop bookingId - A string containing the Booking ID to use.
@prop fulfilmentMethod - An enum of type DeliveryMethods to pass through to other components where it is needed. -->
<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { onMounted, ref, inject, watch, computed, toRefs } from "vue";
import { retailInstanceTypeSymbol } from "@/models/Symbols";
import type { RetailInstanceTypeService } from "@/services/RetailInstanceTypeService";
import type { Ref } from "vue";
import { fetchAuthSession } from "aws-amplify/auth";
import StripeWidget from "@/components/4-Checkout/StripeWidget.vue";
import CheckoutLhs from "@/components/4-Checkout/CheckoutLhs.vue";
import type { CompanyDetails } from "@/models/graphql/CompanyDetailsTypes";
import type { CorporateUserDetails } from "@/models/graphql/CorporateUserTypes";
import type { ManagementInformationQuestionsResponse } from "@/models/graphql/ManagementInformationQuestionsTypes";
import MiQuestions from "@/components/4-Checkout/MiQuestions.vue";
import { getManagementQuestions } from "@/graphql/functions/getManagementQuestions";
import { getCompanyDetails } from "@/graphql/functions/getCompanyDetails";
import { getCorporateUserDetails } from "@/graphql/functions/getCorporateUserDetails";
import { getOrganisationDetails } from "@/graphql/functions/getOrganisation";
import { isMobile } from "@/utilities/IsMobileHelper";
import { useBookingData } from "@/stores/booking-storage";
import { usePaymentStore } from "@/stores/payment";
import { handleError } from "@/services/AlertService";
import { DeliveryMethods } from "@/models/basket/DeliveryMethods";

const router = useRouter();
const props = withDefaults(
    defineProps<{
        bookingId: string;
        fulfilmentMethod?: string;
    }>(),
    {
        bookingId: "",
        fulfilmentMethod: undefined,
    }
);
const userEmail: Ref<string> = ref("");
const isLoading: Ref<boolean> = ref(false);
const { bookingId, fulfilmentMethod } = toRefs(props);
const companyDetails: Ref<CompanyDetails | null> = ref(null);
const userDetails: Ref<CorporateUserDetails | null> = ref(null);
const miQuestions: Ref<ManagementInformationQuestionsResponse | null> = ref(null);
const miQuestionsComponentRef: Ref = ref(null);
const instanceTypeSvc = inject(retailInstanceTypeSymbol) as RetailInstanceTypeService;
const bookingStore = useBookingData();
const paymentStore = usePaymentStore();
const panel: Ref<string | undefined> = ref(undefined);

// Computed property to check if On Account payment option should be available
const showOnAccountOption = computed(
    () =>
        // Ensure dependencies are resolved before calculation
        instanceTypeSvc.isCorporate() && !!companyDetails.value?.hasOnAccountPayment
);

function handleOnAccountSubmit() {
    paymentStore.onSubmitPaymentButtonClick(fulfilmentMethod.value as DeliveryMethods, bookingId.value, null);
}

onMounted(async () => {
    await router.isReady();
    isLoading.value = true;

    try {
        const session = await fetchAuthSession();
        if (session.tokens?.signInDetails?.loginId) {
            userEmail.value = session.tokens.signInDetails.loginId;

            const isCorp = instanceTypeSvc.isCorporate();

            if (isCorp) {
                [companyDetails.value, userDetails.value, miQuestions.value] = await Promise.all([getCompanyDetails(), getCorporateUserDetails(), getManagementQuestions()]);

                const hasOnAccount = companyDetails.value?.hasOnAccountPayment;

                if (hasOnAccount) {
                    const orgId = companyDetails.value?.organisationId || userDetails.value?.organisation?.id;
                    if (orgId) {
                        try {
                            const fetchedOrgDetails = await getOrganisationDetails(orgId);
                            bookingStore.setOrganisationDetails(fetchedOrgDetails);
                        } catch (orgError) {
                            handleError("Could not load organisation details for On Account payment.");
                            bookingStore.setOrganisationDetails(null);
                        }
                    } else {
                        bookingStore.setOrganisationDetails(null);
                    }
                } else {
                    bookingStore.setOrganisationDetails(null);
                }
            }
        } else {
            console.warn("UserPayment: User session not found or invalid.");
        }
    } catch (error) {
        console.error("UserPayment: Error during checkout setup:", error);
        handleError("An error occurred while loading your checkout details. Please try again.");
        companyDetails.value = null;
        userDetails.value = null;
        miQuestions.value = null;
        bookingStore.setOrganisationDetails(null);
    } finally {
        let initialPanelModelValue: string | undefined;
        let initialPaymentMethod: "card" | "onAccount" = "card";

        // Check if On Account option should be displayed
        if (showOnAccountOption.value) {
            // If On Account is available, default to it.
            initialPanelModelValue = "onAccount";
            initialPaymentMethod = "onAccount";
        } else {
            // If On Account is NOT available, only card is possible.
            // The panels component itself won't render due to v-if="showOnAccountOption".
            // So, we only need to ensure the store's state defaults to 'card'.
            initialPaymentMethod = "card";
        }

        // Set the panel model value ONLY if the panels are actually going to be rendered
        if (showOnAccountOption.value) {
            panel.value = initialPanelModelValue;
        }

        // Ensure the store matches the determined initial state
        if (bookingStore.selectedPaymentMethod !== initialPaymentMethod) {
            bookingStore.setPaymentMethod(initialPaymentMethod);
        }

        isLoading.value = false;
    }
});

watch(panel, (newValue) => {
    // Handle the cases based on the string value received
    if (newValue === "onAccount") {
        if (bookingStore.selectedPaymentMethod !== "onAccount") {
            bookingStore.setPaymentMethod("onAccount");
        }
    } else if (newValue === "cardPayment") {
        if (bookingStore.selectedPaymentMethod !== "card") {
            bookingStore.setPaymentMethod("card");
        }
    } else {
        // This case might happen if mandatory somehow fails or during initialization
        // before a value is set. It's safest to default to card if state is unclear.
        // eslint-disable-next-line
        if (bookingStore.selectedPaymentMethod !== "card") {
            bookingStore.setPaymentMethod("card");
        }
    }
});
</script>

<template>
    <div>
        <v-overlay v-model="isLoading" contained class="align-center justify-center" persistent>
            <v-progress-circular indeterminate :size="70" :width="7" color="primary"> </v-progress-circular>
        </v-overlay>

        <v-container>
            <v-row>
                <v-col cols="12" md="6" class="authenticated-checkout">
                    <CheckoutLhs></CheckoutLhs>
                </v-col>
                <v-col cols="12" md="6" class="auth-user-payment">
                    <div class="pb-5" v-if="!isMobile()"></div>
                    <div v-if="miQuestions && (miQuestions.dropdownQuestions?.length || miQuestions.freeTextQuestions?.length)">
                        <h2 class="pb-1">Management information</h2>
                        <MiQuestions :questions="miQuestions" ref="miQuestionsComponentRef"></MiQuestions>
                    </div>

                    <!-- Expansion Panels for On Account vs Card -->
                    <v-expansion-panels :mandatory="true" v-model="panel" elevation="1" eager variant="accordion" v-if="showOnAccountOption">
                        <!-- Panel Order Matters: On Account first (index 0), Card second (index 1) -->
                        <v-expansion-panel key="onAccount" collapse-icon="mdi-radiobox-marked" expand-icon="mdi-radiobox-blank" value="onAccount">
                            <!-- STRING VALUE -->
                            <v-expansion-panel-title>Pay On-Account</v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <div class="pa-2">
                                    <p><img src="https://cdn.mytrainticket.co.uk/wp-content/icons/Diners-Club-Logo.svg" alt="Diners Club International Logo" /></p>
                                    <p class="mt-2" v-if="userDetails?.department">
                                        <font-awesome-icon icon="fa-building" class="mr-2"></font-awesome-icon>
                                        <!-- Changed icon -->
                                        Department: {{ userDetails.department }}
                                    </p>

                                    <v-btn class="mt-4 primary-button payment-on-account-btn" @click="handleOnAccountSubmit()" :loading="isLoading" :disabled="isLoading">
                                        <img class="mr-2 payment-cta-icon" src="https://cdn.mytrainticket.co.uk/wp-content/icons/cc-diners-club-brands_white.svg" alt="Diners Club On-Account" />
                                        Pay with On-Account
                                    </v-btn>
                                    <p class="on-account-disclaimer"><small>By clicking "Pay with On-Account", the total amount will be added to your next Diners Club International statement.</small></p>
                                </div>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                        <v-expansion-panel key="cardPayment" collapse-icon="mdi-radiobox-marked" expand-icon="mdi-radiobox-blank" value="cardPayment">
                            <v-expansion-panel-title>Pay by card</v-expansion-panel-title>
                            <v-expansion-panel-text>
                                <div class="pa-2">
                                    <StripeWidget :booking-id="bookingId" :fulfilmentMethod="fulfilmentMethod"> </StripeWidget>
                                </div>
                            </v-expansion-panel-text>
                        </v-expansion-panel>
                    </v-expansion-panels>

                    <!-- Only Stripe Widget if On Account is not an option -->
                    <StripeWidget v-else :booking-id="bookingId" :fulfilmentMethod="fulfilmentMethod"></StripeWidget>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<style scoped lang="scss">
.v-overlay {
    background-color: rgba(245, 242, 241, 0.9);
    min-height: 70vh;
}

.v-overlay :deep(.v-overlay__scrim) {
    background: none !important;
}

.v-container {
    max-width: 1200px;
}

.auth-user-payment h2 {
    margin-top: 1rem;
}

.on-account-disclaimer {
    margin-top: 10px;
}

.payment-cta-icon {
    max-width: 13%;
    margin-left: 10px;
}

.v-btn--size-default {
    --v-btn-size: 0.875rem;
    --v-btn-height: 100% !important;
    border-radius: 5px;
    text-transform: none;
}

.payment-on-account-btn {
    padding: 15px 25px 15px 20px;
    background: linear-gradient(135deg, #004a98, #01c3a7);
}
</style>
